import { differenceInDays, format } from 'date-fns';
import DateRangePicker, { RangeType, ValueType } from 'rsuite/lib/DateRangePicker';
import EmptyOptimization from '../../../../../../../../../assets/image/empty-optimization.svg';
import { styleFormat } from '../../../../../../../../../utils/FormatNumbers';
import { useHistoryPrices } from '../../../../../../hooks/useHistoryPrices';
import { BigNumbers } from '../../../BigNumbers';
import { DateRangePickerCustom } from '../../../DateRangePickerCustom/DateRangePickerCustom';
import { HistoryFilters } from '../../../Filters';
import { FilterStore } from '../../../FilterStore/FilterStore';
import { HistogramChart } from '../../../HistogramChart';
import { TableHistory } from '../../../TableHistory';
import styles from './HistoryPricesTab.module.scss';

import moment from 'moment';

const optionsPercent = {
  style: 'percent' as styleFormat,
  options: {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    showSuffix: true,
  },
};

const optionsDecimal = {
  style: 'decimal' as styleFormat,
  options: {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    showSuffix: false,
  },
};

export const dateShortcut: RangeType[] = [
  {
    label: 'Últimos 7 dias',
    value: [moment().subtract(7, 'days').toDate(), moment().toDate()],
  },
  {
    label: 'Últimos 30 dias',
    value: [moment().subtract(30, 'days').toDate(), moment().toDate()],
  },
  {
    label: 'Últimos 90 dias',
    value: [moment().subtract(90, 'days').toDate(), moment().toDate()],
  },
];

export const HistoryPricesTab = () => {
  const { handlers, states } = useHistoryPrices();
  const {
    isLoadingHistoryBigNumbers,
    history,
    stores,
    isFetchingHistoryChart,
    isCompletedFilter,
    isFetchingHistoryTableData,
    storesIdWithoutResults,
    hasHistoryPricesData,
    isLoadingHistoryPricesData,
    optimizationStatus,
  } = states;

  const { start, end } = optimizationStatus;
  const { handleSelectPeriodFilter, handleSelectStoreId } = handlers;

  const { before, combine, after, afterToday } = DateRangePicker;
  const { bigNumbers } = history.data;

  if (!hasHistoryPricesData && !isLoadingHistoryPricesData) {
    return (
      <div className={styles.empty}>
        <span className={styles.title}>Nenhum histórico disponível até o momento.</span>
        <span className={styles.subtitle}>Os dados de histórico serão exibidos após a aplicação dos preços otimizados.</span>
        <img src={EmptyOptimization} alt="" />
      </div>
    );
  }

  const disabledDate = combine(before(new Date(start)), after(end) || afterToday());

  const diffDays =
    history.filters.periodSelected?.length === 2
      ? differenceInDays(new Date(history.filters.periodSelected[1]), new Date(history.filters.periodSelected[0]))
      : null;
  return (
    <main className={styles.container}>
      <section className={styles['big-numbers-container']}>
        <div className={styles['title-container']}>
          <span className={styles.title}>Histórico de Preços</span>
          <span className={styles.subtitle}>Acompanhe a aderência e a aplicação dos preços sugeridos no período selecionado.</span>
        </div>
        <div className={styles.period}>
          <div className={styles['calendar-wrapper']}>
            <div className={styles['period-container']}>
              <span className={styles.label}>Período: </span>
              <DateRangePickerCustom
                block
                cleanable
                // @ts-ignore
                renderValue={(value: ValueType) => {
                  if (!value) return;
                  if (Array.isArray(value) && value.length >= 2 && value[0] !== undefined && value[1] !== undefined) {
                    return (
                      <div className={styles['date-range']}>
                        <span>{format(value[0], 'dd/MM/yyyy')}</span>
                        {' ~ '}
                        <span>{format(value[1], 'dd/MM/yyyy')}</span>
                      </div>
                    );
                  } else {
                    return null;
                  }
                }}
                value={history.filters.periodSelected as ValueType}
                onChange={value => handleSelectPeriodFilter(value as Date[])}
                onOk={value => handleSelectPeriodFilter(value as Date[])}
                onClean={() => handleSelectPeriodFilter([])}
                appearance="default"
                placeholder="Selecione um período"
                format="DD/MM/YYYY"
                ranges={dateShortcut}
                disabledDate={(date, selectValue, doneSelected) => {
                  return disabledDate(date, selectValue, doneSelected);
                }}
              />
            </div>
            {diffDays && diffDays < 7 ? <p className={styles.error}>Selecione no mínimo 7 dias</p> : null}
          </div>

          <div className={styles['filter-store']}>
            <span className={styles.label}>Loja otimização: </span>
            <FilterStore
              handleSelectStoreId={handleSelectStoreId}
              stores={stores}
              selectedStoresId={history.filters.storesSelected}
              storesIdWithoutResults={storesIdWithoutResults}
              cleanable={false}
            />
          </div>
        </div>
        <div className={styles['big-numbers']}>
          <BigNumbers
            isLoading={isLoadingHistoryBigNumbers}
            styleNumber={optionsPercent}
            mainValue={bigNumbers.adhesion}
            description="de aderência à otimização entre preços sugeridos e aplicados"
            positiveVariation={bigNumbers.adhesion >= 0}
            highlight
          />
          <BigNumbers
            isLoading={isLoadingHistoryBigNumbers}
            styleNumber={optionsDecimal}
            mainValue={bigNumbers.pricesApplied}
            description="preços aplicados ao total no período selecionado"
            positiveVariation={bigNumbers.pricesApplied >= 0}
            highlight
          />
        </div>
      </section>

      <HistoryFilters />

      {isCompletedFilter && <HistogramChart isLoading={isFetchingHistoryChart} />}
      {isCompletedFilter && <TableHistory isLoading={isFetchingHistoryTableData} />}
    </main>
  );
};
